import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa';
import logo from '../../assets/images/Tandara-Logo-Cleaned.png';

const HeaderContainer = styled.header`
  background-color: ${({ theme }) => theme.colors.primary};
  box-shadow: ${({ theme }) => theme.shadows.small};
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 1rem 0;
  width: 100%;
`;

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  width: 100%;
  position: relative;
`;

const Logo = styled.img`
  height: 60px;
  width: auto;
  max-width: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 45px;
  }

  @media (max-width: 390px) {
    height: 40px;
  }
`;

const NavLinks = styled.nav`
  display: flex;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.medium};
    padding: 1rem;
    z-index: 10;
    width: 100vw;
    margin-left: -1rem;
    max-height: calc(100vh - 100px);
    overflow-y: auto;
  }
`;

const DropdownIndicator = styled.span`
  margin-left: 5px;
  font-size: 0.7em;
`;

const MobileDropdownContent = styled.div`
  position: relative;
  box-shadow: none;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.05);
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  padding: 0.5rem 0;
  margin: 0.5rem 0;
  border-radius: 4px;
`;

const DesktopDropdownContent = styled.div`
  display: none;
  position: absolute;
  background-color: ${({ theme }) => theme.colors.background};
  min-width: 160px;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  z-index: 1;
  border-radius: ${({ theme }) => theme.borderRadius.small};
`;

const NavItem = styled.div`
  position: relative;
  margin: 0 1.25rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    &:hover > ${DesktopDropdownContent} {
      display: block;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin: 0.5rem 0;
    width: 100%;
    text-align: center;
  }
`;

const NavLink = styled(Link)`
  color: ${({ theme }) => theme.colors.background};
  font-weight: 500;
  padding: 0.5rem;
  transition: color 0.3s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #d1d8e0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0.75rem 1rem;
    width: 100%;
    justify-content: space-between;
  }
`;

const DropdownLink = styled(Link)`
  color: ${({ theme }) => theme.colors.text};
  padding: 0.75rem 1rem;
  display: block;
  text-align: left;
  transition: background-color 0.3s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${({ theme }) => theme.colors.lightBackground};
    color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    text-align: left;
    color: ${({ theme }) => theme.colors.background};
    padding: 0.5rem 2rem;
    margin-left: 1rem;
    font-size: 0.9rem;
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
      color: ${({ theme }) => theme.colors.background};
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.background};
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0.5rem;
  border-radius: 4px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  svg {
    transition: color 0.3s ease;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
  }
`;

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);
  const [dropdowns, setDropdowns] = useState({
    home: false,
    services: false,
    fees: false,
    news: false,
    policies: false,
  });
  const headerRef = useRef(null);

  // Add resize event listener to handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 992;
      setIsMobile(mobile);
      
      // Close mobile menu and dropdowns when switching from mobile to desktop
      if (!mobile) {
        setMobileMenuOpen(false);
        closeAllDropdowns();
      }
    };

    // Set initial state
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
        closeAllDropdowns();
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    if (!mobileMenuOpen) {
      closeAllDropdowns();
    }
  };

  const toggleDropdown = (dropdown) => {
    setDropdowns({
      ...dropdowns,
      [dropdown]: !dropdowns[dropdown],
    });
  };

  const closeAllDropdowns = () => {
    setDropdowns({
      home: false,
      services: false,
      fees: false,
      news: false,
      policies: false,
    });
  };

  const handleLinkClick = () => {
    setMobileMenuOpen(false);
    closeAllDropdowns();
  };

  return (
    <HeaderContainer ref={headerRef}>
      <NavContainer>
        <Link to="/" onClick={handleLinkClick}>
          <Logo src={logo} alt="Tandara Lodge" />
        </Link>

        <MobileMenuButton onClick={toggleMobileMenu}>
          {mobileMenuOpen ? <FaTimes /> : <FaBars />}
        </MobileMenuButton>

        <NavLinks isOpen={mobileMenuOpen}>
          <NavItem>
            <NavLink 
              to="/" 
              onClick={(e) => {
                if (isMobile) {
                  e.preventDefault();
                  toggleDropdown('home');
                } else {
                  handleLinkClick();
                }
              }}
            >
              Home
              <DropdownIndicator><FaChevronDown /></DropdownIndicator>
            </NavLink>
            {isMobile ? (
              <MobileDropdownContent isOpen={dropdowns.home}>
                <DropdownLink to="/vision-mission" onClick={handleLinkClick}>
                  Vision & Mission
                </DropdownLink>
                <DropdownLink to="/location" onClick={handleLinkClick}>
                  Location
                </DropdownLink>
              </MobileDropdownContent>
            ) : (
              <DesktopDropdownContent>
                <DropdownLink to="/vision-mission" onClick={handleLinkClick}>
                  Vision & Mission
                </DropdownLink>
                <DropdownLink to="/location" onClick={handleLinkClick}>
                  Location
                </DropdownLink>
              </DesktopDropdownContent>
            )}
          </NavItem>

          <NavItem>
            <NavLink 
              to="/services" 
              onClick={(e) => {
                if (isMobile) {
                  e.preventDefault();
                  toggleDropdown('services');
                } else {
                  handleLinkClick();
                }
              }}
            >
              Services
              <DropdownIndicator><FaChevronDown /></DropdownIndicator>
            </NavLink>
            {isMobile ? (
              <MobileDropdownContent isOpen={dropdowns.services}>
                <DropdownLink to="/services/residential" onClick={handleLinkClick}>
                  Residential
                </DropdownLink>
                <DropdownLink to="/services/respite" onClick={handleLinkClick}>
                  Respite
                </DropdownLink>
                <DropdownLink to="/services/independent-living" onClick={handleLinkClick}>
                  Independent Living
                </DropdownLink>
              </MobileDropdownContent>
            ) : (
              <DesktopDropdownContent>
                <DropdownLink to="/services/residential" onClick={handleLinkClick}>
                  Residential
                </DropdownLink>
                <DropdownLink to="/services/respite" onClick={handleLinkClick}>
                  Respite
                </DropdownLink>
                <DropdownLink to="/services/independent-living" onClick={handleLinkClick}>
                  Independent Living
                </DropdownLink>
              </DesktopDropdownContent>
            )}
          </NavItem>

          <NavItem>
            <NavLink 
              to="/fees" 
              onClick={(e) => {
                if (isMobile) {
                  e.preventDefault();
                  toggleDropdown('fees');
                } else {
                  handleLinkClick();
                }
              }}
            >
              Fees & Charges
              <DropdownIndicator><FaChevronDown /></DropdownIndicator>
            </NavLink>
            {isMobile ? (
              <MobileDropdownContent isOpen={dropdowns.fees}>
                <DropdownLink to="/fees" onClick={handleLinkClick}>
                  Fees Details
                </DropdownLink>
              </MobileDropdownContent>
            ) : (
              <DesktopDropdownContent>
                <DropdownLink to="/fees" onClick={handleLinkClick}>
                  Fees Details
                </DropdownLink>
              </DesktopDropdownContent>
            )}
          </NavItem>

          <NavItem>
            <NavLink 
              to="/news" 
              onClick={(e) => {
                if (isMobile) {
                  e.preventDefault();
                  toggleDropdown('news');
                } else {
                  handleLinkClick();
                }
              }}
            >
              News & Events
              <DropdownIndicator><FaChevronDown /></DropdownIndicator>
            </NavLink>
            {isMobile ? (
              <MobileDropdownContent isOpen={dropdowns.news}>
                <DropdownLink to="/news" onClick={handleLinkClick}>
                  News
                </DropdownLink>
                <DropdownLink to="/events" onClick={handleLinkClick}>
                  Events
                </DropdownLink>
              </MobileDropdownContent>
            ) : (
              <DesktopDropdownContent>
                <DropdownLink to="/news" onClick={handleLinkClick}>
                  News
                </DropdownLink>
                <DropdownLink to="/events" onClick={handleLinkClick}>
                  Events
                </DropdownLink>
              </DesktopDropdownContent>
            )}
          </NavItem>

          <NavItem>
            <NavLink 
              to="/policies" 
              onClick={(e) => {
                if (isMobile) {
                  e.preventDefault();
                  toggleDropdown('policies');
                } else {
                  handleLinkClick();
                }
              }}
            >
              Policies
              <DropdownIndicator><FaChevronDown /></DropdownIndicator>
            </NavLink>
            {isMobile ? (
              <MobileDropdownContent isOpen={dropdowns.policies}>
                <DropdownLink to="/privacy" onClick={handleLinkClick}>
                  Privacy
                </DropdownLink>
              </MobileDropdownContent>
            ) : (
              <DesktopDropdownContent>
                <DropdownLink to="/privacy" onClick={handleLinkClick}>
                  Privacy
                </DropdownLink>
              </DesktopDropdownContent>
            )}
          </NavItem>

          <NavItem>
            <NavLink to="/links" onClick={handleLinkClick}>
              Links
            </NavLink>
          </NavItem>

          <NavItem>
            <NavLink to="/contact" onClick={handleLinkClick}>
              Contact Us
            </NavLink>
          </NavItem>
        </NavLinks>
      </NavContainer>
    </HeaderContainer>
  );
};

export default Header;
